/* SSi – "Audience" sections for the online-teachers sales page.
 *
 * Rebuilt 2026-07-13 from Aran's redraft (gwefan/updates/tutors.md).
 * Sections here, in page order:
 *   1. ResultsEvidence   – results-retain-students + Cardiff study band
 *                          (replaces the cut pain section; Cardiff copy
 *                          mirrors the schools pages' research feature)
 *   2. TheAnswer         – the "six days in between" spine, with the
 *                          week diagram merged in (old BetweenLessons)
 *   3. Mechanism         – what a session actually is (4-step loop,
 *                          same shape as SchoolsMechanism)
 *   4. InAnyLanguage     – the absolute-beginners unlock
 *   5. CleanConscience   – how we treat the students they recommend us to
 *   6. BuiltByTeachers   – Tom + Aran, aligned with the schools pages
 *                          (Catrin's card and the 50+ years stat cut)
 *   7. FoundingTeachers  – honest be-early section (replaces invented
 *                          teacher testimonials)
 *   8. FirstWeek         – signup-to-first-payout on-ramp
 *
 * Style discipline: no stock icons, no gradient slop. Words do most of
 * the work; the only graphic is the week diagram inside TheAnswer.
 * Background rhythm (hero cream → results off-white → research red →
 * answer cream → mechanism off-white → lang white → maths cream →
 * conscience white → built cream → celebs red → founding off-white →
 * first-week white) alternates without introducing new palette.
 */
import { TEACHER_SETUP_URL } from './TeachersOnline.jsx';

/* -------------------------------------------------------------- *
 * 1. Results & evidence – replaces the pain section              *
 * -------------------------------------------------------------- */

export function ResultsEvidence({ t }) {
  return (
    <>
      <section style={resStyles.wrap}>
        <div style={resStyles.inner}>
          <div style={resStyles.kicker}>{t.toResKicker}</div>
          <h2 style={resStyles.h2}>{t.toResTitle}</h2>
          <p style={resStyles.body}>{t.toResBody1}</p>
          <p style={{ ...resStyles.body, marginBottom: 0 }}>{t.toResBody2}</p>
        </div>
      </section>

      <section className="ssi-toa-rf" style={rfStyles.wrap}>
        <div className="ssi-toa-rf__inner" style={rfStyles.inner}>
          <div>
            <div style={rfStyles.kicker}>{t.toResearchKicker}</div>
            <h2 className="ssi-toa-rf__h2" style={rfStyles.h2}>{t.toResearchTitle}</h2>
            <p style={rfStyles.body}>{t.toResearchBody}</p>
            <a href="https://orca.cardiff.ac.uk/id/eprint/174145/" target="_blank" rel="noopener" style={rfStyles.link}>
              {t.toProofResearchLink} →
            </a>
          </div>
          <figure className="ssi-toa-rf__quote" style={rfStyles.quoteCard}>
            <blockquote style={rfStyles.quote}>&ldquo;{t.toResearchQuote}&rdquo;</blockquote>
            <figcaption style={rfStyles.cite}>{t.toResearchCite}</figcaption>
          </figure>
        </div>
      </section>
    </>
  );
}

const resStyles = {
  wrap: { background:'var(--offWhite, #FDF5F5)', padding:'80px 32px' },
  inner: { maxWidth:860, margin:'0 auto' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 46px/1.12 var(--font-display)', margin:'0 0 24px', color:'var(--fg-1)', letterSpacing:'-.015em', textWrap:'balance' },
  body: { font:'400 18px/1.6 var(--font-body)', color:'var(--fg-2)', margin:'0 0 18px', textWrap:'pretty' },
};

const rfStyles = {
  wrap: { background:'var(--brandRed)', padding:'72px 32px' },
  inner: { maxWidth:1160, margin:'0 auto', display:'grid', gridTemplateColumns:'1.15fr 1fr', gap:56, alignItems:'center' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--gold)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 44px/1.12 var(--font-display)', margin:'0 0 18px', color:'#fff', letterSpacing:'-.01em' },
  body: { font:'400 18px/1.55 var(--font-body)', color:'rgba(255,255,255,.92)', margin:'0 0 22px' },
  link: { display:'inline-block', background:'#fff', color:'var(--brandRed)', fontSize:15, fontWeight:700, textDecoration:'none', padding:'12px 22px', borderRadius:999 },
  quoteCard: { margin:0, background:'rgba(255,255,255,.1)', border:'1px solid rgba(255,255,255,.25)', borderLeft:'4px solid var(--gold)', borderRadius:16, padding:'30px 32px' },
  quote: { font:'400 22px/1.45 var(--font-display)', fontStyle:'italic', margin:'0 0 14px', color:'#fff' },
  cite: { fontSize:13, color:'rgba(255,255,255,.75)' },
};

/* -------------------------------------------------------------- *
 * 2. The answer – six days in between, week diagram merged in    *
 * -------------------------------------------------------------- */

export function TheAnswer({ t }) {
  const answers = [
    { tag: t.toAnsTag1, title: t.toAnsT1, body: t.toAnsB1 },
    { tag: t.toAnsTag2, title: t.toAnsT2, body: t.toAnsB2 },
    { tag: t.toAnsTag3, title: t.toAnsT3, body: t.toAnsB3 },
  ];
  const days = [
    { label:t.toWeekDay1, lesson:false },
    { label:t.toWeekDay2, lesson:true  },
    { label:t.toWeekDay3, lesson:false },
    { label:t.toWeekDay4, lesson:false },
    { label:t.toWeekDay5, lesson:true  },
    { label:t.toWeekDay6, lesson:false },
    { label:t.toWeekDay7, lesson:false },
  ];
  return (
    <section style={answerStyles.wrap}>
      <div style={answerStyles.inner}>
        <div style={answerStyles.head}>
          <div style={answerStyles.kicker}>{t.toAnsKicker}</div>
          <h2 style={answerStyles.h2}>{t.toAnsTitle}</h2>
          <p style={answerStyles.lede}>{t.toAnsLede}</p>
        </div>

        <div style={weekStyles.panel}>
          <div className="ssi-toa-week__grid" style={weekStyles.weekGrid}>
            {days.map((d, i) => (
              <div key={i} style={weekStyles.dayCol}>
                <div style={weekStyles.dayLabel}>{d.label}</div>
                <div style={{
                  ...weekStyles.lessonSlot,
                  ...(d.lesson ? weekStyles.lessonSlotActive : {}),
                }}>
                  {d.lesson && <span style={weekStyles.lessonText}>{t.toWeekLessonLabel}</span>}
                </div>
                <div style={weekStyles.ssiSlot}>
                  <div style={weekStyles.ssiDot}/>
                </div>
              </div>
            ))}
          </div>
          <div style={weekStyles.legend}>
            <span style={weekStyles.legendItem}>
              <span style={{ ...weekStyles.legendSwatch, background:'var(--gold)' }}/>
              {t.toWeekLessonLabel}
            </span>
            <span style={weekStyles.legendItem}>
              <span style={{ ...weekStyles.legendSwatch, background:'rgba(255,255,255,.9)', border:'1px solid rgba(255,255,255,.4)' }}/>
              {t.toWeekSsiLabel}
            </span>
          </div>
          <p style={weekStyles.freedom}>{t.toWeekFreedom}</p>
        </div>

        <ul className="ssi-toa-answer__grid" style={answerStyles.grid}>
          {answers.map((a, i) => (
            <li key={i} style={answerStyles.card}>
              <div style={answerStyles.tag}>{a.tag}</div>
              <h3 style={answerStyles.cardH}>{a.title}</h3>
              <p style={answerStyles.cardB}>{a.body}</p>
            </li>
          ))}
        </ul>

        <p style={answerStyles.closer}>{t.toAnsCloser}</p>
      </div>
    </section>
  );
}

const answerStyles = {
  wrap: { background:'var(--bg-cream)', padding:'80px 32px' },
  inner: { maxWidth:1160, margin:'0 auto' },
  head: { maxWidth:820, marginBottom:44 },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 56px/1.05 var(--font-display)', margin:'0 0 20px', color:'var(--fg-1)', letterSpacing:'-.02em', textWrap:'balance' },
  lede: { font:'400 20px/1.55 var(--font-body)', color:'var(--fg-2)', margin:0, textWrap:'pretty' },

  grid: {
    listStyle:'none', margin:'48px 0 0', padding:0,
    display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:24,
  },
  card: {
    background:'#fff', border:'1px solid var(--border)',
    borderRadius:20, padding:'36px 32px 40px',
    display:'flex', flexDirection:'column', gap:14,
    boxShadow:'0 1px 2px rgba(0,0,0,.03), 0 18px 40px -28px rgba(73,3,0,.18)',
  },
  tag: {
    alignSelf:'flex-start',
    fontSize:11, letterSpacing:'.14em', textTransform:'uppercase',
    color:'var(--brandRed)', fontWeight:700,
    background:'var(--pastelRed, #FDF0EF)',
    padding:'6px 10px', borderRadius:999,
  },
  cardH: {
    font:'700 26px/1.2 var(--font-display)',
    margin:0, color:'var(--fg-1)', letterSpacing:'-.01em', textWrap:'balance',
  },
  cardB: {
    font:'400 16px/1.55 var(--font-body)',
    color:'var(--fg-2)', margin:0, textWrap:'pretty',
  },

  closer: {
    font:'500 22px/1.4 var(--font-display)',
    color:'var(--fg-1)', letterSpacing:'-.005em',
    margin:'56px 0 0', maxWidth:820, textWrap:'balance',
  },
};

const weekStyles = {
  panel: {
    background:'var(--brandRed)', color:'#fff', borderRadius:24,
    padding:'40px 36px 34px',
    boxShadow:'0 24px 56px -28px rgba(178,25,45,.5)',
  },
  weekGrid: {
    display:'grid', gridTemplateColumns:'repeat(7, 1fr)', gap:14,
    marginBottom:24,
  },
  dayCol: { display:'flex', flexDirection:'column', alignItems:'stretch', gap:10 },
  dayLabel: {
    fontSize:12, letterSpacing:'.14em', textTransform:'uppercase',
    color:'rgba(255,255,255,.6)', fontWeight:700, textAlign:'center',
  },
  lessonSlot: {
    height:56, borderRadius:10,
    border:'1px dashed rgba(255,255,255,.2)',
    display:'flex', alignItems:'center', justifyContent:'center',
  },
  lessonSlotActive: {
    background:'var(--gold)', border:'1px solid var(--gold)',
  },
  lessonText: {
    fontSize:12, fontWeight:700, color:'var(--fg-1)',
    letterSpacing:'.02em', textTransform:'uppercase',
  },
  ssiSlot: {
    height:44, borderRadius:10, background:'rgba(255,255,255,.08)',
    border:'1px solid rgba(255,255,255,.18)',
    display:'flex', alignItems:'center', justifyContent:'center',
  },
  ssiDot: { width:10, height:10, borderRadius:'50%', background:'rgba(255,255,255,.92)' },

  legend: {
    display:'flex', gap:24, justifyContent:'center',
    paddingTop:4, fontSize:13, color:'rgba(255,255,255,.82)',
  },
  legendItem: { display:'inline-flex', alignItems:'center', gap:8 },
  legendSwatch: { width:14, height:14, borderRadius:4, display:'inline-block' },

  freedom: {
    font:'400 16px/1.6 var(--font-body)', margin:'28px auto 0',
    maxWidth:720, color:'rgba(255,255,255,.88)', textAlign:'center',
    textWrap:'pretty',
  },
};

/* -------------------------------------------------------------- *
 * 3. What actually happens – the session loop                    *
 *    (carries the #how anchor for the hero's "How it works")     *
 * -------------------------------------------------------------- */

export function Mechanism({ t }) {
  const steps = [
    { title:t.toMechStep1T, body:t.toMechStep1B },
    { title:t.toMechStep2T, body:t.toMechStep2B },
    { title:t.toMechStep3T, body:t.toMechStep3B },
    { title:t.toMechStep4T, body:t.toMechStep4B },
  ];
  return (
    <section id="how" className="ssi-toa-mech" style={mechStyles.wrap}>
      <div style={mechStyles.inner}>
        <div style={mechStyles.head}>
          <div style={mechStyles.kicker}>{t.toMechKicker}</div>
          <h2 style={mechStyles.h2}>{t.toMechTitle}</h2>
          <p style={mechStyles.lede}>{t.toMechLede}</p>
        </div>
        <ol className="ssi-toa-mech__grid" style={mechStyles.grid}>
          {steps.map((s, i) => (
            <li key={i} style={mechStyles.step}>
              <div style={mechStyles.stepNum}>{i + 1}</div>
              <h3 style={mechStyles.stepH}>{s.title}</h3>
              <p style={mechStyles.stepB}>{s.body}</p>
            </li>
          ))}
        </ol>
        <div style={mechStyles.maths}>{t.toMechMaths}</div>
        <p style={mechStyles.closer}>{t.toMechCloser}</p>
      </div>
    </section>
  );
}

const mechStyles = {
  wrap: { background:'var(--offWhite, #FDF5F5)', padding:'88px 32px' },
  inner: { maxWidth:1160, margin:'0 auto' },
  head: { textAlign:'center', maxWidth:760, margin:'0 auto 48px' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 46px/1.1 var(--font-display)', margin:'0 0 16px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  lede: { font:'400 18px/1.55 var(--font-body)', color:'var(--fg-2)', margin:0 },
  grid: { listStyle:'none', margin:0, padding:0, display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:16 },
  step: { background:'#fff', border:'1px solid var(--border)', borderRadius:16, padding:'26px 24px' },
  stepNum: { width:36, height:36, borderRadius:'50%', background:'var(--brandRed)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', font:'700 17px/1 var(--font-display)', marginBottom:16 },
  stepH: { font:'700 21px/1.25 var(--font-display)', margin:'0 0 10px', color:'var(--fg-1)' },
  stepB: { fontSize:15, lineHeight:1.55, color:'var(--fg-2)', margin:0 },
  maths: { background:'var(--pastelRed, #FDF0EF)', borderRadius:16, padding:'28px 32px', margin:'32px auto 0', maxWidth:920, fontSize:17, lineHeight:1.6, color:'var(--fg-1)' },
  closer: { textAlign:'center', font:'400 21px/1.5 var(--font-display)', fontStyle:'italic', color:'var(--fg-1)', margin:'40px auto 0', maxWidth:720 },
};

/* -------------------------------------------------------------- *
 * 4. In any language – English through the student's own L1      *
 * -------------------------------------------------------------- */

export function InAnyLanguage({ t }) {
  // Each entry: {label: English name, native: endonym, script: 'latin'|'cjk'|'rtl'|'indic'}
  const langs = [
    { label: t.toLangMandarin,   native: '中文',        script:'cjk' },
    { label: t.toLangSpanish,    native: 'español',    script:'latin' },
    { label: t.toLangFrench,     native: 'français',   script:'latin' },
    { label: t.toLangGerman,     native: 'Deutsch',    script:'latin' },
    { label: t.toLangItalian,    native: 'italiano',   script:'latin' },
    { label: t.toLangPortuguese, native: 'português',  script:'latin' },
    { label: t.toLangArabic,     native: 'العربية',     script:'rtl' },
    { label: t.toLangJapanese,   native: '日本語',      script:'cjk' },
    { label: t.toLangKorean,     native: '한국어',      script:'cjk' },
    { label: t.toLangHindi,      native: 'हिन्दी',       script:'indic' },
    { label: t.toLangBengali,    native: 'বাংলা',       script:'indic' },
    { label: t.toLangGujarati,   native: 'ગુજરાતી',     script:'indic' },
    { label: t.toLangPunjabi,    native: 'ਪੰਜਾਬੀ',      script:'indic' },
    { label: t.toLangTamil,      native: 'தமிழ்',       script:'indic' },
    { label: t.toLangUrdu,       native: 'اردو',        script:'rtl' },
    { label: t.toLangMarathi,    native: 'मराठी',       script:'indic' },
    { label: t.toLangTelugu,     native: 'తెలుగు',      script:'indic' },
  ];

  const points = [
    { h: t.toLangPoint1H, b: t.toLangPoint1B },
    { h: t.toLangPoint2H, b: t.toLangPoint2B },
    { h: t.toLangPoint3H, b: t.toLangPoint3B },
  ];

  return (
    <section style={langStyles.wrap}>
      <div style={langStyles.inner}>
        <div style={langStyles.head}>
          <div style={langStyles.kicker}>{t.toLangKicker}</div>
          <h2 style={langStyles.h2}>{t.toLangTitle}</h2>
          <p style={langStyles.lede}>{t.toLangLede}</p>
        </div>

        <div className="ssi-toa-lang__grid" style={langStyles.grid}>
          {/* Left: proof points */}
          <ul style={langStyles.points}>
            {points.map((p, i) => (
              <li key={i} style={langStyles.point}>
                <span style={langStyles.pointNum}>{String(i + 1).padStart(2, '0')}</span>
                <div>
                  <div style={langStyles.pointH}>{p.h}</div>
                  <p style={langStyles.pointB}>{p.b}</p>
                </div>
              </li>
            ))}
          </ul>

          {/* Right: language chips */}
          <div style={langStyles.chipsWrap}>
            <div style={langStyles.chipsLabel}>{t.toLangChipsLabel}</div>
            <ul style={langStyles.chips}>
              {langs.map((l, i) => (
                <li key={i} style={langStyles.chip}>
                  <span style={langStyles.chipEn}>English</span>
                  <span style={langStyles.chipArrow} aria-hidden>←</span>
                  <span style={{
                    ...langStyles.chipNative,
                    ...(l.script === 'cjk' ? { fontSize:19, letterSpacing:0 } : {}),
                    ...(l.script === 'rtl' ? { direction:'rtl', unicodeBidi:'embed' } : {}),
                    ...(l.script === 'indic' ? { fontStyle:'normal', fontSize:16 } : {}),
                  }}>{l.native}</span>
                  <span style={langStyles.chipEnName}>({l.label})</span>
                </li>
              ))}
              <li style={{ ...langStyles.chip, ...langStyles.chipSoon }}>
                <span style={langStyles.chipSoonText}>{t.toLangMore}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

const langStyles = {
  wrap: { background:'#fff', padding:'96px 32px 104px' },
  inner: { maxWidth:1160, margin:'0 auto' },

  head: { maxWidth:820, marginBottom:56 },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 56px/1.05 var(--font-display)', margin:'0 0 20px', color:'var(--fg-1)', letterSpacing:'-.02em', textWrap:'balance' },
  lede: { font:'400 20px/1.55 var(--font-body)', color:'var(--fg-2)', margin:0, textWrap:'pretty' },

  grid: {
    display:'grid',
    gridTemplateColumns:'minmax(0, 1fr) minmax(0, 1fr)',
    gap:64,
    alignItems:'start',
  },

  /* Left column – three numbered proof points */
  points: { listStyle:'none', margin:0, padding:0, display:'flex', flexDirection:'column', gap:32 },
  point: { display:'grid', gridTemplateColumns:'auto 1fr', columnGap:20, alignItems:'baseline' },
  pointNum: {
    font:'500 15px/1 var(--font-display)',
    color:'var(--brandRed)', letterSpacing:'.06em',
    fontVariantNumeric:'tabular-nums',
  },
  pointH: {
    font:'700 22px/1.25 var(--font-display)',
    color:'var(--fg-1)', letterSpacing:'-.01em',
    margin:'0 0 8px', textWrap:'balance',
  },
  pointB: {
    font:'400 16px/1.6 var(--font-body)',
    color:'var(--fg-2)', margin:0, textWrap:'pretty',
  },

  /* Right column – language chips */
  chipsWrap: {
    background:'var(--bg-cream, #FAF4EC)',
    border:'1px solid var(--border)',
    borderRadius:20,
    padding:'28px 28px 32px',
  },
  chipsLabel: {
    fontSize:11, letterSpacing:'.16em', textTransform:'uppercase',
    color:'var(--brandRed)', fontWeight:700,
    marginBottom:18,
  },
  chips: {
    listStyle:'none', margin:0, padding:0,
    display:'flex', flexWrap:'wrap', gap:8,
  },
  chip: {
    display:'inline-flex', alignItems:'center', gap:8,
    background:'#fff',
    border:'1px solid var(--border)',
    borderRadius:999,
    padding:'9px 14px',
    font:'500 14px/1 var(--font-body)',
    color:'var(--fg-1)',
    whiteSpace:'nowrap',
  },
  chipEn: { color:'var(--fg-1)', fontWeight:600 },
  chipArrow: { color:'var(--brandRed)', fontWeight:600, fontSize:13 },
  chipNative: { color:'var(--fg-2)', fontStyle:'italic' },
  chipEnName: { color:'var(--grey)', fontSize:12.5 },
  chipSoon: {
    background:'transparent',
    borderStyle:'dashed',
    borderColor:'var(--border-strong, #D9CEC2)',
  },
  chipSoonText: {
    color:'var(--grey)', fontStyle:'italic', letterSpacing:'.01em',
  },
};

/* -------------------------------------------------------------- *
 * 5. Recommend it with a clean conscience                        *
 * -------------------------------------------------------------- */

export function CleanConscience({ t }) {
  return (
    <section className="ssi-toa-cc" style={ccStyles.wrap}>
      <div style={ccStyles.panel}>
        <div style={ccStyles.kicker}>{t.toTrustKicker}</div>
        <h2 style={ccStyles.h2}>{t.toTrustTitle}</h2>
        <p style={ccStyles.body}>{t.toTrustBody1}</p>
        <p style={ccStyles.body}>{t.toTrustBody2}</p>
        <a href="#sample" style={ccStyles.cta}>{t.toTrustCta} →</a>
      </div>
    </section>
  );
}

const ccStyles = {
  wrap: { background:'#fff', padding:'88px 32px' },
  panel: {
    maxWidth:860, margin:'0 auto',
    background:'var(--pastelRed, #FDF0EF)',
    border:'1px solid var(--border)',
    borderRadius:24, padding:'52px 56px',
  },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 38px/1.15 var(--font-display)', margin:'0 0 20px', color:'var(--fg-1)', letterSpacing:'-.01em', textWrap:'balance' },
  body: { font:'400 17px/1.6 var(--font-body)', color:'var(--fg-2)', margin:'0 0 16px', textWrap:'pretty' },
  cta: { display:'inline-block', marginTop:6, color:'var(--brandRed)', fontSize:16, fontWeight:700, textDecoration:'none' },
};

/* -------------------------------------------------------------- *
 * 6. Built by teachers – aligned with the schools pages          *
 * -------------------------------------------------------------- */

export function BuiltByTeachers({ t }) {
  // toBuiltImgN (optional) shows a photo instead of the initials circle,
  // same convention as SchoolsBuilt.
  const people = [
    { name:t.toBuiltName1, role:t.toBuiltRole1, img:t.toBuiltImg1 },
    { name:t.toBuiltName2, role:t.toBuiltRole2, img:t.toBuiltImg2 },
  ].filter(p => p.name).map(p => ({ ...p, initials:(p.name||'').trim()[0] || '' }));
  return (
    <section className="ssi-toa-built" style={builtStyles.wrap}>
      <div className="ssi-toa-built__inner" style={builtStyles.inner}>
        <div style={builtStyles.copy}>
          <div style={builtStyles.kicker}>{t.toBuiltKicker}</div>
          <h2 style={builtStyles.h2}>{t.toBuiltTitle}</h2>
          <p style={builtStyles.body}>{t.toBuiltBody}</p>
        </div>

        <div style={builtStyles.people}>
          {people.map((p, i) => (
            <div key={i} style={builtStyles.person}>
              {p.img
                ? <img src={p.img} alt={p.name} style={builtStyles.photo}/>
                : <span style={builtStyles.avatar}>{p.initials}</span>}
              <span>
                <span style={builtStyles.name}>{p.name}</span>
                <span style={builtStyles.role}>{p.role}</span>
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const builtStyles = {
  wrap: { background:'var(--bg-cream)', padding:'80px 32px' },
  inner: {
    maxWidth:1080, margin:'0 auto',
    display:'grid', gridTemplateColumns:'1.1fr 1fr', gap:56, alignItems:'center',
  },
  copy: {},
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 40px/1.12 var(--font-display)', margin:'0 0 20px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  body: { font:'400 18px/1.6 var(--font-body)', color:'var(--fg-2)', margin:0, textWrap:'pretty' },

  people: { display:'flex', flexDirection:'column', gap:14 },
  person: { display:'flex', alignItems:'center', gap:16, background:'#fff', border:'1px solid var(--border)', borderRadius:14, padding:'16px 20px' },
  avatar: { width:48, height:48, borderRadius:'50%', background:'var(--brandRed)', color:'#fff', display:'inline-flex', alignItems:'center', justifyContent:'center', font:'700 20px/1 var(--font-display)', flexShrink:0 },
  photo: { width:48, height:48, borderRadius:'50%', objectFit:'cover', objectPosition:'50% 25%', flexShrink:0, display:'block' },
  name: { display:'block', fontSize:16, fontWeight:600, color:'var(--fg-1)' },
  role: { display:'block', fontSize:13, color:'var(--grey)', marginTop:2 },
};

/* -------------------------------------------------------------- *
 * 7. Founding teachers – honest be-early section                 *
 * -------------------------------------------------------------- */

export function FoundingTeachers({ t }) {
  return (
    <section style={ftStyles.wrap}>
      <div style={ftStyles.inner}>
        <div style={ftStyles.kicker}>{t.toFirstKicker}</div>
        <h2 style={ftStyles.h2}>{t.toFirstTitle}</h2>
        <p style={ftStyles.body}>{t.toFirstBody1}</p>
        <p style={ftStyles.body}>{t.toFirstBody2}</p>
        <div style={ftStyles.ctaRow}>
          <a href={TEACHER_SETUP_URL} className="btn btn--primary">{t.toFirstCta}</a>
        </div>
      </div>
    </section>
  );
}

const ftStyles = {
  wrap: { background:'var(--offWhite, #FDF5F5)', padding:'88px 32px' },
  inner: { maxWidth:760, margin:'0 auto', textAlign:'center' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 44px/1.1 var(--font-display)', margin:'0 0 24px', color:'var(--fg-1)', letterSpacing:'-.01em' },
  body: { font:'400 17px/1.6 var(--font-body)', color:'var(--fg-2)', margin:'0 0 18px', textAlign:'left' },
  ctaRow: { marginTop:14 },
};

/* -------------------------------------------------------------- *
 * 8. Your first week – signup to first payout                    *
 * -------------------------------------------------------------- */

export function FirstWeek({ t }) {
  const steps = [
    { title:t.toWeek1Step1T, body:t.toWeek1Step1B },
    { title:t.toWeek1Step2T, body:t.toWeek1Step2B },
    { title:t.toWeek1Step3T, body:t.toWeek1Step3B },
    { title:t.toWeek1Step4T, body:t.toWeek1Step4B },
  ];
  return (
    <section className="ssi-toa-w1" style={w1Styles.wrap}>
      <div style={w1Styles.inner}>
        <div style={w1Styles.head}>
          <div style={w1Styles.kicker}>{t.toWeek1Kicker}</div>
          <h2 style={w1Styles.h2}>{t.toWeek1Title}</h2>
        </div>
        <ol className="ssi-toa-w1__grid" style={w1Styles.grid}>
          {steps.map((s, i) => (
            <li key={i} style={w1Styles.step}>
              <div style={w1Styles.stepNum}>{i + 1}</div>
              <h3 style={w1Styles.stepH}>{s.title}</h3>
              <p style={w1Styles.stepB}>{s.body}</p>
            </li>
          ))}
        </ol>
        <p style={w1Styles.closer}>{t.toWeek1Closer}</p>
      </div>
    </section>
  );
}

const w1Styles = {
  wrap: { background:'#fff', padding:'88px 32px' },
  inner: { maxWidth:1160, margin:'0 auto' },
  head: { textAlign:'center', maxWidth:720, margin:'0 auto 48px' },
  kicker: { fontSize:12, letterSpacing:'.16em', textTransform:'uppercase', color:'var(--brandRed)', fontWeight:700, marginBottom:14 },
  h2: { font:'700 46px/1.1 var(--font-display)', margin:0, color:'var(--fg-1)', letterSpacing:'-.01em' },
  grid: { listStyle:'none', margin:0, padding:0, display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:16 },
  step: { background:'var(--offWhite, #FDF5F5)', border:'1px solid var(--border)', borderRadius:16, padding:'26px 24px' },
  stepNum: { width:36, height:36, borderRadius:'50%', background:'var(--brandRed)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', font:'700 17px/1 var(--font-display)', marginBottom:16 },
  stepH: { font:'700 21px/1.25 var(--font-display)', margin:'0 0 10px', color:'var(--fg-1)' },
  stepB: { fontSize:15, lineHeight:1.55, color:'var(--fg-2)', margin:0 },
  closer: { textAlign:'center', font:'500 20px/1.5 var(--font-display)', color:'var(--fg-1)', margin:'40px auto 0', maxWidth:720, textWrap:'balance' },
};
